import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Square } from "lucide-react";
import { Logo } from "@/components/Logo";
import { AccountControl } from "@/components/auth/AccountControl";
import { useSession } from "@/context/SessionContext";

const LINKS = [
  { label: "How it works", to: "/how-it-works" },
  { label: "Pricing", to: "/pricing" },
  { label: "Refer", to: "/referrals" },
  { label: "Blog", to: "/blog" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { live, endSession } = useSession();

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className="fixed top-0 inset-x-0 z-[70] bg-ink border-b border-white/5"
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-3 sm:px-8 h-[72px] flex items-center justify-between font-body tracking-normal">
        <Link to="/" data-testid="nav-logo-link" aria-label="Jiggly home">
          <Logo />
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.slice(1).replace("/", "-")}`}
              className={`font-body text-sm font-medium tracking-normal transition-colors hover:text-neon ${
                pathname === l.to ? "text-neon" : "text-mist"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {live && <button
            onClick={endSession}
            data-testid="nav-end-session-button"
            aria-label="End Session"
            title="End Session"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-semibold text-xs sm:text-sm p-2.5 sm:px-4 transition-colors hover:border-neon hover:text-neon"
          >
            <Square size={14} strokeWidth={2.5} />
            <span className="hidden sm:inline">End Session</span>
          </button>}
          <AccountControl />
          <button
            className="lg:hidden text-white p-1 sm:p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="lg:hidden overflow-hidden glass border-t border-white/5"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link to={l.to} className="font-body font-medium tracking-normal text-xl text-white transition-colors hover:text-neon" data-testid={`nav-mobile-link-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              {live && <button
                onClick={() => {
                  setOpen(false);
                  endSession();
                }}
                data-testid="nav-mobile-end-session-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-neon text-ink font-body font-semibold px-5 py-3 transition-colors hover:bg-neonhover"
              >
                <Square size={16} strokeWidth={2.5} />
                End Session
              </button>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
